/**
 * TutorialController — step machine that runs each fixed frame.
 *
 * Observes GameState, checks success conditions, emits GameEffects for the
 * tutorial overlay, and advances steps on completion.
 *
 * 13 steps (0-12): power ramp (0-6) → reversal (7) → survival (8-11) → payoff (12).
 */

import { Input, Keys, Time } from 'bonkjs';
import type { GameState } from '../types';
import type { GameEffect } from '../game/effects';
import { transitionPhase } from '../game/effects';
import { TUTORIAL } from '../constants';
import { TUTORIAL_STEPS, type TutorialStep, DEFENSE_START_STEP, OFFENSE_STEP_COUNT, DEFENSE_PREUNLOCK_KEYS } from './steps';
import { setupTutorialStep, fireOpponentShot } from './setup';
import { fireRailgunBeam } from '../game/phase-edgeguard';
import { DEFAULT_STAGE_ID } from '../stages';
import { msToFrames } from '../game/input-edge';
import { addCoins, hasUnlock, loadProgression, saveProgression } from '../progression/storage';
import { COIN_AWARD } from '../progression/catalog';

export class TutorialController {
  // ─── Per-step tracking ──────────────────────────────────────────────────
  /** Snapshot of player X at step start — for movement detection. */
  private stepStartX = 0;
  /** Whether the player activated lift (boost) this step. */
  private liftActivated = false;
  /** Whether the player fired this step. */
  private hasFired = false;
  /** Shot tier that was fired. */
  private firedTier = 0;
  /** Whether the opponent was hit this step (damage increased). */
  private opponentWasHit = false;
  /** Previous opponent damage — to detect hits. */
  private prevOpponentDamage = 0;
  /** Whether edgeguard beam was fired this step. */
  private edgeguardWasFired = false;
  /** Whether player has recovered from tumble. */
  private hasRecovered = false;
  /** Whether the player was hit (damage increased) this step. */
  private playerWasHit = false;
  /** Previous player damage — to detect incoming hits. */
  private prevPlayerDamage = 0;
  /** Whether the player pressed A or D during DI window. */
  private diInputDetected = false;
  /** Whether the opponent was KO'd this step. */
  private opponentKOd = false;
  /** Whether the opponent was alive at step start (for KO detection). */
  private opponentWasAlive = true;
  /** Whether burst resolved (burstFlying or burstBraced triggered). */
  private burstWasResolved = false;
  /** Whether the step just advanced (for one-frame setup). */
  private justAdvanced = true;
  /** Whether the player survived the edgeguard phase (di_strategic step). */
  private edgeguardSurvived = false;

  // ─── Incoming step state ────────────────────────────────────────────────
  /** Frames into the incoming step (for delayed opponent fire). */
  private incomingTimer = 0;
  /** Whether the opponent has fired in the incoming step. */
  private incomingFired = false;
  /** Number of times the opponent's shot has missed and been retried. */
  private incomingRetries = 0;

  // ─── Freeze-frame teaching state ────────────────────────────────────────
  /** Remaining freeze time for DI/recovery teaching pause. */
  private teachingFreezeTimer = 0;
  /** Whether the teaching freeze has been applied for this step. */
  private teachingFreezeApplied = false;

  // ─── Guided edgeguard freeze ──────────────────────────────────────────
  /** Whether the defender is currently frozen for the guided edgeguard step. */
  private edgeguardFreezeActive = false;

  // ─── Edgeguard auto-fire tracking ─────────────────────────────────────
  /** Whether the AI opponent has auto-fired the railgun in di_strategic. */
  private edgeguardAutoFired = false;
  /** Previous phase — for detecting phase transitions (edgeguard → defensive_movement). */
  private prevPhase: string = 'aiming';

  /** Initialize or reset the controller for a fresh tutorial run. */
  reset(state: GameState): void {
    const ms = state.modeState;
    const isDefense = ms.tutorialVariant === 'defense';
    ms.tutorialStep = isDefense ? DEFENSE_START_STEP : 0;
    ms.tutorialStepFrame = state.frameCount;
    ms.tutorialComplete = false;
    ms.tutorialSuccessHold = 0;
    ms.tutorialUnlockedKeys = isDefense ? [...DEFENSE_PREUNLOCK_KEYS] : [];
    ms.tutorialEdgeguardFreeze = false;
    this.justAdvanced = true;
    this.resetStepTracking(state);
  }

  /** Called each fixed frame after updatePhase(). Returns effects for tutorial overlay. */
  update(state: GameState): GameEffect[] {
    const fx: GameEffect[] = [];
    const ms = state.modeState;
    if (ms.mode !== 'tutorial' || ms.tutorialComplete) return fx;

    const step = TUTORIAL_STEPS[ms.tutorialStep];
    if (!step) return fx;

    // Tick opponent fade-in after reposition (~0.25s)
    if (ms.tutorialOpponentFade < 1) {
      ms.tutorialOpponentFade = Math.min(1, ms.tutorialOpponentFade + Time.fixedDeltaTime * 4);
    }

    // Tutorial dummy drift-back: opponent air-drifts toward stage center when airborne
    const dummy = state.players[1];
    if (dummy?.alive && !dummy.grounded && !dummy.settled && dummy.hitstunFrames <= 0) {
      const centerX = (state.stageLeft + state.stageRight) / 2;
      const driftSpeed = 0.8;
      if (dummy.x > centerX + 20) {
        dummy.vx = Math.max(dummy.vx - driftSpeed, -3);
      } else if (dummy.x < centerX - 20) {
        dummy.vx = Math.min(dummy.vx + driftSpeed, 3);
      }
    }

    // One-frame setup on step entry
    if (this.justAdvanced) {
      this.justAdvanced = false;
      setupTutorialStep(state, ms.tutorialStep);
      this.resetStepTracking(state);

      // Freeze-frame teaching steps: apply brief freeze on entry
      if (step.freezeOnEntry) {
        this.teachingFreezeTimer = step.id === 'di_basics'
          ? TUTORIAL.DI_FREEZE_SECONDS
          : TUTORIAL.RECOVERY_FREEZE_SECONDS;
        this.teachingFreezeApplied = true;
        // Use freezeUntil to pause game physics
        state.freezeUntil = Math.max(
          state.freezeUntil,
          state.frameCount + msToFrames(this.teachingFreezeTimer * 1000),
        );
      }

      // Steps that need a clean slate: force-transition to aiming and clean up active defense/DI state
      if (step.id === 'kill' || step.id === 'burst' || step.id === 'di_strategic' || step.id === 'burst_escape' || step.id === 'win_battle') {
        transitionPhase(state, 'aiming', fx);
        state.defenseActivePlayer = -1;
        state.defenseQueue = [];
        state.diActivePlayer = -1;
        state.diHitQueue = [];
        state.projectiles = [];
        state.deferredSpawns = [];
        state.freezeUntil = 0;
        // Reset player charge/fire state for clean aiming
        const p = state.players[0];
        p.power = 0;
        p.charging = false;
        p.fireHeld = false;
        p.chargeGraceTimer = 0;
        p.chargeGracePower = 0;
        p.selectedShotTier = 1;
      }

      fx.push({
        type: 'tutorial_prompt',
        text: step.promptText,
        subText: step.subText,
        inputHints: step.inputHints,
      });
      fx.push({ type: 'tutorial_step_advance', step: ms.tutorialStep });
    }

    // Check if in success hold (brief pause after success)
    if (ms.tutorialSuccessHold > 0) {
      ms.tutorialSuccessHold -= Time.fixedDeltaTime;
      if (ms.tutorialSuccessHold <= 0) {
        this.advanceStep(state, fx);
      }
      return fx;
    }

    // Guard: opponentTurn steps must keep currentPlayer = 1
    if (step.opponentTurn && state.currentPlayer !== 1 && !this.playerWasHit) {
      state.currentPlayer = 1;
    }

    // Handle incoming step — opponent fires after a delay
    if (step.id === 'incoming_hit' || step.id === 'di_strategic') {
      this.updateIncoming(state, fx, step.id === 'di_strategic');
    }

    // Guided edgeguard freeze — freeze defender physics when edgeguard starts
    if (step.id === 'hit_edgeguard') {
      if (state.phase === 'edgeguard' && !this.edgeguardFreezeActive) {
        // Activate freeze: zero defender velocity, set flag
        this.edgeguardFreezeActive = true;
        ms.tutorialEdgeguardFreeze = true;
        for (const di of state.edgeguardKnockedIndices) {
          const def = state.players[di];
          if (def?.alive) {
            def.vx = 0;
            def.vy = 0;
          }
        }
      }
      // Clear freeze once attacker fires the beam
      if (this.edgeguardFreezeActive && state.edgeguardFired) {
        this.edgeguardFreezeActive = false;
        ms.tutorialEdgeguardFreeze = false;
      }
    }

    // di_strategic: auto-fire railgun when charge completes (AI opponent)
    if (step.id === 'di_strategic' && state.phase === 'edgeguard' && !this.edgeguardAutoFired) {
      if (state.edgeguardChargeFrame >= state.edgeguardChargeTarget && state.edgeguardChargeTarget > 0) {
        this.edgeguardAutoFired = true;
        const attacker = state.players[state.currentPlayer];
        if (attacker) {
          fireRailgunBeam(state, attacker, fx);
        }
      }
    }

    // di_strategic: detect edgeguard survived — phase left edgeguard and player 0 alive
    if (step.id === 'di_strategic') {
      if (this.prevPhase === 'edgeguard' && state.phase !== 'edgeguard') {
        if (state.players[0]?.alive) {
          this.edgeguardSurvived = true;
        }
      }
    }
    this.prevPhase = state.phase;

    // Track state changes for condition checking
    this.trackStateChanges(state, step);

    // Check success condition
    if (this.checkCondition(state, step)) {
      const hold = step.holdAfterSuccess ?? TUTORIAL.SUCCESS_HOLD_SECONDS;
      if (hold > 0) {
        ms.tutorialSuccessHold = hold;
        fx.push({ type: 'tutorial_success' });
      } else {
        this.advanceStep(state, fx);
      }
    }

    return fx;
  }

  /** Advance to the next step or complete the tutorial. */
  private advanceStep(state: GameState, fx: GameEffect[]): void {
    const ms = state.modeState;
    const nextStep = ms.tutorialStep + 1;
    const maxStep = ms.tutorialVariant === 'offense' ? OFFENSE_STEP_COUNT : TUTORIAL_STEPS.length;

    if (nextStep >= maxStep) {
      // Tutorial variant complete
      ms.tutorialComplete = true;
      ms.tutorialStepFrame = state.frameCount; // stamp for auto-return timer
      // Award coins once per variant (offense/defense tracked separately)
      const unlockKey = 'tutorial:' + (ms.tutorialVariant ?? 'offense');
      if (!hasUnlock(unlockKey)) {
        addCoins(COIN_AWARD.TUTORIAL);
        const data = loadProgression();
        data.unlocks.push(unlockKey);
        saveProgression(data);
      }
      fx.push({ type: 'tutorial_complete' });
      return;
    }

    ms.tutorialStep = nextStep;
    ms.tutorialStepFrame = state.frameCount;
    this.justAdvanced = true;
  }

  /** Reset per-step tracking variables. */
  private resetStepTracking(state: GameState): void {
    const player = state.players[0];
    const opponent = state.players[1];
    this.stepStartX = player?.x ?? 0;
    this.liftActivated = false;
    this.hasFired = false;
    this.firedTier = 0;
    this.opponentWasHit = false;
    this.prevOpponentDamage = opponent?.damagePercent ?? 0;
    this.edgeguardWasFired = false;
    this.hasRecovered = false;
    this.playerWasHit = false;
    this.prevPlayerDamage = player?.damagePercent ?? 0;
    this.diInputDetected = false;
    this.opponentKOd = false;
    this.opponentWasAlive = opponent?.alive ?? true;
    this.burstWasResolved = false;
    this.edgeguardSurvived = false;
    this.edgeguardAutoFired = false;
    this.incomingTimer = 0;
    this.incomingFired = false;
    this.incomingRetries = 0;
    this.teachingFreezeTimer = 0;
    this.teachingFreezeApplied = false;
    this.edgeguardFreezeActive = false;
    this.prevPhase = 'aiming';
  }

  /** Track state changes each frame for condition evaluation. */
  private trackStateChanges(state: GameState, _step: TutorialStep): void {
    const player = state.players[0];
    const opponent = state.players[1];

    // Lift tracking (boost)
    if (player.liftActive) {
      this.liftActivated = true;
    }

    // Fired tracking (detect phase transition to 'firing')
    if (state.phase === 'firing') {
      this.hasFired = true;
      this.firedTier = player.selectedShotTier;
    }

    // Opponent hit tracking (damage increased)
    if (opponent && opponent.damagePercent > this.prevOpponentDamage) {
      this.opponentWasHit = true;
    }

    // Edgeguard tracking
    if (state.phase === 'edgeguard' && state.edgeguardFired) {
      this.edgeguardWasFired = true;
    }

    // Player hit tracking (damage increased — for incoming step)
    if (player.damagePercent > this.prevPlayerDamage) {
      this.playerWasHit = true;
    }

    // DI input tracking — detect raw A/D press during DI window.
    // Uses raw Input (not diAngleShift) because horizontal knockback can produce
    // zero perpendicular shift even when the player is pressing A/D.
    if (state.phase === 'di_window' && state.diActivePlayer === 0) {
      if (Input.getKey(Keys.A) || Input.getKey(Keys.D)) {
        this.diInputDetected = true;
      }
    }

    // Recovery tracking — player landed (not tumbling, on the ground)
    if (_step.id === 'recovery' && !player.tumble && player.grounded) {
      this.hasRecovered = true;
    }

    // Burst resolve tracking — burstFlying or burstBraced
    if (player.burstFlying || player.burstBraced) {
      this.burstWasResolved = true;
    }

    // Opponent KO tracking
    if (opponent && !opponent.alive && this.opponentWasAlive) {
      this.opponentKOd = true;
    }
  }

  /** Handle the "incoming" step — opponent fires at the player after a delay. */
  private updateIncoming(state: GameState, _fx: GameEffect[], keepEdgeguard = false): void {
    if (this.incomingFired) {
      // Safety: shot was fired but missed — retry (max 3 attempts, then force-hit)
      if (!this.playerWasHit
          && state.projectiles.length === 0
          && state.deferredSpawns.length === 0
          && state.phase !== 'firing') {
        this.incomingRetries++;
        if (this.incomingRetries >= 3) {
          // Give up retrying — credit the hit so the step can advance
          this.playerWasHit = true;
          return;
        }
        this.incomingFired = false;
        this.incomingTimer = TUTORIAL.INCOMING_CHARGE_FRAMES - 15; // short delay before retry
        state.currentPlayer = 1; // keep opponent in control
        transitionPhase(state, 'aiming', _fx);
      }
      return;
    }

    this.incomingTimer++;

    // After a brief delay (1 second), fire the opponent's shot
    if (this.incomingTimer >= TUTORIAL.INCOMING_CHARGE_FRAMES) {
      this.incomingFired = true;
      fireOpponentShot(state, keepEdgeguard);
      // Transition to firing phase so the projectile plays out
      transitionPhase(state, 'firing', _fx);
    }
  }

  /** Check if the current step's success condition is met. */
  private checkCondition(state: GameState, step: TutorialStep): boolean {
    const player = state.players[0];

    switch (step.condition.type) {
      case 'player_moved': {
        const dx = Math.abs(player.x - this.stepStartX);
        return dx >= step.condition.threshold;
      }
      case 'player_lifted':
        return this.liftActivated;
      case 'player_fired':
        return this.hasFired;
      case 'opponent_hit':
        return this.opponentWasHit;
      case 'player_fired_tier':
        return this.hasFired && this.firedTier >= step.condition.tier;
      case 'opponent_tumbling': {
        const opponent = state.players[1];
        return !!opponent?.tumble;
      }
      case 'edgeguard_fired':
        return this.edgeguardWasFired;
      case 'player_hit':
        return this.playerWasHit;
      case 'di_input_detected':
        return this.diInputDetected;
      case 'di_window_resolved':
        return state.phase !== 'firing' && state.phase !== 'di_window';
      case 'edgeguard_survived':
        return this.edgeguardSurvived;
      case 'player_recovered':
        return this.hasRecovered;
      case 'burst_resolved':
        return this.burstWasResolved;
      case 'opponent_ko':
        return this.opponentKOd;
    }
  }

  /** Handle tutorial-specific pause menu. Returns true if pause was handled. */
  handlePause(state: GameState, action: 'resume' | 'skip' | 'restart' | 'menu', fx: GameEffect[]): void {
    switch (action) {
      case 'resume':
        state.paused = false;
        break;
      case 'skip': {
        state.paused = false;
        const ms = state.modeState;
        ms.tutorialEdgeguardFreeze = false;
        this.edgeguardFreezeActive = false;
        const nextStep = ms.tutorialStep + 1;
        const maxStep = ms.tutorialVariant === 'offense' ? OFFENSE_STEP_COUNT : TUTORIAL_STEPS.length;
        if (nextStep >= maxStep) {
          ms.tutorialComplete = true;
          fx.push({ type: 'tutorial_complete' });
        } else {
          ms.tutorialStep = nextStep;
          ms.tutorialStepFrame = state.frameCount;
          ms.tutorialSuccessHold = 0;
          this.justAdvanced = true;
        }
        break;
      }
      case 'restart':
        state.paused = false;
        this.reset(state);
        break;
      case 'menu':
        state.paused = false;
        transitionPhase(state, 'title_screen', fx);
        fx.push({ type: 'stage_theme_change', stageId: DEFAULT_STAGE_ID });
        break;
    }
  }
}
